import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import Eyebrow from '../components/ui/Eyebrow';
import HeroBackground from '../components/ui/HeroBackground';
import ManagerAvatar from '../components/ui/ManagerAvatar';
import { CastFigure, Depth, Reveal } from '../motion';
import {
  COURT_CASES,
  FRANCHISES,
  PENDING_RANKS,
  PENDING_TEASER,
  SEALED_TAKES,
  TAPE_DESK,
  TAPE_RANKS,
  ranksFromLast,
} from '../data/theTape';
import TapeReveal from '../components/tape/TapeReveal';
import TapeColdOpen from '../components/tape/TapeColdOpen';
import TapeRankSting from '../components/tape/TapeRankSting';
import { managerSlug } from '../utils/slug';

function LiveBug() {
  return (
    <span className="inline-flex items-center gap-2 font-display font-black tracking-[0.28em] text-[10px] uppercase text-accent-red">
      <span className="tape-live-dot" aria-hidden="true" />
      Live desk
    </span>
  );
}

function RankStamp({ rank }) {
  return (
    <div className="font-display font-black leading-none text-accent-rust tabular-nums" style={{ fontSize: 'clamp(76px, 11vw, 148px)' }}>
      {rank}
    </div>
  );
}

function SideColumn({ label, tone, status, children }) {
  const border = tone === 'red' ? 'border-accent-red' : 'border-accent-gold';
  const text = tone === 'red' ? 'text-accent-red' : 'text-accent-gold';
  const wash = tone === 'red' ? 'bg-accent-red/[0.06]' : 'bg-accent-gold/[0.09]';
  return (
    <div className={`min-w-0 border-t-[3px] ${border} ${wash} p-4`}>
      <div className="flex items-center justify-between gap-3">
        <div className={`font-display tracking-[0.28em] text-[10px] uppercase font-bold ${text}`}>
          {label}
        </div>
        <div className="font-mono text-[9px] tracking-[0.18em] uppercase text-ink-cream-strong/55">
          {status}
        </div>
      </div>
      <p className="mt-3 font-body italic text-[13px] md:text-sm leading-relaxed text-ink-cream-strong/85">
        {children}
      </p>
    </div>
  );
}

function RankCard({ entry, isNew = false }) {
  const sequence = entry.rank === 10
    ? ['facepalm', 'shock']
    : entry.rank === 3
      ? ['smug', 'point']
      : entry.rank === 2
        ? ['celebrate', 'point']
        : entry.rank === 1
          ? ['celebrate', 'smug']
          : ['shock', 'point', 'smug'];
  const percentFootnote = entry.rank === 5
    ? entry.exhibits.find((item) => item.includes('forgot to say it'))
    : null;

  return (
    <Reveal className={`pt-8 md:pt-12 ${isNew ? 'rank-new rank-new--animate' : ''}`}>
      <Depth
        as="article"
        id={`rank-${entry.rank}`}
        data-testid={`tape-rank-${entry.rank}`}
        className="paper mx-auto max-w-6xl overflow-visible border-t-4 border-accent-rust"
        strength={0.35}
        style={{ overflow: 'visible' }}
      >
        <div className="relative z-10 grid grid-cols-1 gap-5 px-4 pb-6 pt-4 sm:px-6 md:grid-cols-[minmax(170px,0.34fr)_minmax(0,1fr)] md:gap-8 md:px-8 md:pb-8">
          <div className="relative min-w-0" data-depth="0.4">
            <div className="absolute right-0 top-0 opacity-20" aria-hidden="true">
              <RankStamp rank={entry.rank} />
            </div>
            <div className="relative z-10 flex items-end gap-3 md:block">
              <CastFigure
                name={entry.manager}
                trigger="inview"
                sequence={sequence}
                className="-mt-[10%] aspect-[4/5] w-32 shrink-0 sm:w-40 md:w-full md:max-w-[230px]"
                imgClassName="drop-shadow-[0_18px_16px_rgba(61,40,23,0.25)]"
              />
              <Link to={`/managers/${managerSlug(entry.manager)}`} className="relative mb-3 block min-w-0 md:mb-0 md:mt-2">
                <div className="font-display font-black uppercase tracking-normal text-2xl leading-none text-ink-cream-strong md:text-3xl">
                  {entry.manager}
                </div>
                <div className="mt-1 font-mono text-[9px] tracking-[0.16em] uppercase text-ink-cream-strong/60 md:text-[10px]">
                  {entry.teamName}
                </div>
                <div className="mt-2 font-display font-black text-accent-rust text-xl leading-none">
                  {entry.teichPct || 'No % yet'}
                </div>
                {percentFootnote && (
                  <p className="mt-2 max-w-[28ch] font-mono text-[9px] leading-relaxed text-ink-cream-strong/60">
                    {percentFootnote}
                  </p>
                )}
              </Link>
            </div>
          </div>
          <div className="min-w-0 pt-1" data-depth="0.2">
            <div className="font-mono text-[9px] font-bold uppercase tracking-[0.25em] text-accent-rust">
              Rank stamp {entry.rank}
            </div>
            <h2 className="mt-2 font-display font-black uppercase leading-[0.92] tracking-normal text-3xl text-ink-cream-strong md:text-4xl">
              {entry.headline}
            </h2>
            <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
              <SideColumn label="Teichmann" tone="gold" status={entry.teichStatus.split('-').join(' ')}>
                {entry.teich}
              </SideColumn>
              <SideColumn label="Krow" tone="red" status={entry.krowStatus}>
                {entry.krow}
              </SideColumn>
            </div>
            <ul className="mt-5 space-y-1.5 border-t border-accent-rust/25 pt-4">
              {entry.exhibits.map((item, index) => (
                <li key={item} className="grid grid-cols-[auto_minmax(0,1fr)] gap-2 font-mono text-[10px] leading-relaxed text-ink-cream-strong/70">
                  <span className="font-bold text-accent-rust">EX.{index + 1}</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            {entry.rank === 3 && <TapeRankSting />}
          </div>
        </div>
      </Depth>
    </Reveal>
  );
}

function PendingSlot({ rank }) {
  return (
    <div id={`rank-${rank}`} className="flex min-w-0 items-center justify-center gap-2 border border-accent-red/35 bg-black/70 px-2 py-4 sm:gap-3 sm:px-4">
      <div className="flex aspect-square w-8 shrink-0 items-center justify-center rounded-full border-2 border-red-950 bg-accent-red font-display text-lg font-black leading-none text-bg-deep shadow-[0_5px_12px_rgba(0,0,0,0.5)] sm:w-10">
        {rank}
      </div>
      <div className="min-w-0 font-display tracking-[0.12em] text-[9px] font-black uppercase text-ink-secondary sm:text-[11px] sm:tracking-[0.2em]">
        Still filming
      </div>
    </div>
  );
}

const SEEN_KEY = 'b09_seen_tape_ranks';

function readSeenRanks() {
  try { return JSON.parse(window.localStorage.getItem(SEEN_KEY)) || []; } catch (e) { return []; }
}

export default function TheTape() {
  const board = ranksFromLast();
  const [searchParams, setSearchParams] = useSearchParams();
  const revealRank = Number(searchParams.get('reveal'));
  const revealEntry = TAPE_RANKS.find((r) => r.rank === revealRank);
  const [revealing, setRevealing] = useState(!!revealEntry);
  // A rank the visitor has not seen before gets the unseal treatment, once.
  const newRanks = useMemo(() => {
    const seen = readSeenRanks();
    return board.map((r) => r.rank).filter((r) => !seen.includes(r));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    try { window.localStorage.setItem(SEEN_KEY, JSON.stringify(board.map((r) => r.rank))); } catch (e) { /* private mode */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const firstVisit = newRanks.length >= board.length;

  return (
    <>
      {revealing && revealEntry && (
        <TapeReveal
          rank={revealRank}
          onDone={() => { setRevealing(false); setSearchParams({}, { replace: true }); }}
        />
      )}
      <HeroBackground backdrop="studio" variant="red" className="px-6 md:px-14 pt-10 pb-14">
        <div className="flex items-center justify-between gap-4">
          <LiveBug />
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-ink-tertiary">
            {TAPE_DESK.asOfLabel}
          </div>
        </div>
        <Eyebrow color="red" className="mt-6">Vol XIV · Preseason desk</Eyebrow>
        <h1 className="mt-4 font-display font-black uppercase leading-[0.86] tracking-normal"
            style={{ fontSize: 'clamp(48px, 8vw, 104px)' }}>
          The Tape
          <span className="block text-accent-red">{TAPE_DESK.boardComplete ? 'is complete.' : 'is rolling.'}</span>
        </h1>
        <p className="mt-6 max-w-[46ch] font-body italic text-xl md:text-2xl text-ink-secondary leading-snug">
          Kyle Teichmann ranked the league from last to first with a homemade model.
          AJ Krow reacted like it is a national desk. Ten spots are on the record.
          The camera bag is empty.
        </p>
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl">
          <HeroChip value={`${TAPE_DESK.publishedCount}/10`} label="Ranks on tape" />
          <HeroChip value="2 desks" label="Teich vs Krow" />
          <HeroChip value={`${COURT_CASES.length} dockets`} label="Fantasy court" />
          <HeroChip value="Not official" label="Keepers still unlocked" />
        </div>
      </HeroBackground>

      <TapeColdOpen />

      <section className="px-6 md:px-14 py-12 md:py-16 bg-bg-deep">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <div className="font-display tracking-[0.35em] text-[11px] text-accent-gold font-bold uppercase">
              Worst to first
            </div>
            <h2 className="mt-2 font-display font-black uppercase leading-none text-4xl md:text-5xl">
              The board.
            </h2>
          </div>
          <p className="max-w-sm font-body italic text-sm text-ink-tertiary">
            Percentages are Kyle’s. Dissents are AJ’s. Neither is a league ruling.
            Ten of ten. The board is closed.
          </p>
        </div>
        <div className="mt-6 space-y-1">
          {board.map((entry) => (
            <RankCard key={entry.rank} entry={entry} isNew={!firstVisit && newRanks.includes(entry.rank)} />
          ))}
        </div>
        <div className="mt-8 grid grid-cols-3 gap-1 border border-border bg-black p-1 md:gap-2 md:p-2" data-testid="tape-pending-ranks">
          {TAPE_DESK.boardComplete ? (
            <div className="col-span-3 flex min-w-0 items-center justify-center gap-3 px-4 py-5 font-display text-[12px] font-black uppercase tracking-[0.24em] text-accent-gold">
              The board is complete. Ten of ten on tape.
            </div>
          ) : (
            PENDING_RANKS.map((rank) => (
              <PendingSlot key={rank} rank={rank} />
            ))
          )}
        </div>
        {!TAPE_DESK.boardComplete && (
          <p className="mt-3 font-body italic text-sm text-ink-tertiary">{PENDING_TEASER}</p>
        )}
      </section>

      <section className="px-6 md:px-14 py-12 md:py-16">
        <div className="font-display tracking-[0.35em] text-[11px] text-accent-red font-bold uppercase">
          Fantasy court
        </div>
        <h2 className="mt-2 font-display font-black uppercase leading-none text-4xl md:text-5xl">
          All rise.
        </h2>
        <p className="mt-4 max-w-2xl font-body italic text-ink-secondary">
          Nine public cases from this week’s tape. No invented evidence. No confidential
          group-chat leaks. The court only hears what Kyle, AJ, Josh, and Amir put on camera.
        </p>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
          {COURT_CASES.map((entry) => (
            <Reveal as="article" key={entry.docket} className="bg-bg-cream text-ink-cream-strong p-5 md:p-6">
              <div className="flex items-center justify-between gap-3 font-mono text-[10px] tracking-[0.16em] uppercase text-accent-rust">
                <span>{entry.docket}</span>
                <span>{entry.status}</span>
              </div>
              <h3 className="mt-3 font-display font-black uppercase leading-none text-2xl">
                {entry.title}
              </h3>
              <div className="mt-2 font-display tracking-[0.2em] text-[10px] uppercase font-bold">
                v. {entry.defendant}
              </div>
              <p className="mt-4 font-body italic text-sm leading-relaxed">{entry.charge}</p>
              <p className="mt-3 font-body text-[12px] leading-relaxed text-ink-cream-strong/80">
                Exhibit: {entry.exhibit}
              </p>
              <div className="mt-4 border-t border-accent-rust/30 pt-3 font-display text-[11px] uppercase tracking-[0.16em] font-bold">
                {entry.recommendedSentence}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-14 py-12 md:py-16 bg-bg-deep">
        <div className="font-display tracking-[0.35em] text-[11px] text-accent-gold font-bold uppercase">
          Franchise office
        </div>
        <h2 className="mt-2 font-display font-black uppercase leading-none text-4xl md:text-5xl">
          Relocations.
        </h2>
        <p className="mt-4 max-w-2xl font-body italic text-ink-secondary">
          AJ held a stadium press conference on a reaction pod. Josh offered Kyle new team names
          from an airport lounge. This is the museum of those bits.
        </p>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
          {FRANCHISES.map((entry) => (
            <Reveal as="article" key={entry.manager} className="border border-border p-5">
              <div className="flex items-center gap-3">
                <ManagerAvatar name={entry.manager} size="sm" />
                <div>
                  <div className="font-display font-black uppercase leading-none">{entry.manager}</div>
                  <div className="mt-1 font-mono text-[10px] tracking-[0.16em] uppercase text-accent-gold">
                    {entry.current}
                  </div>
                </div>
              </div>
              <div className="mt-4 font-mono text-[11px] text-ink-tertiary leading-relaxed">
                {entry.lineage.join(' → ')}
              </div>
              <p className="mt-3 font-body italic text-sm text-ink-secondary leading-relaxed">
                {entry.note}
              </p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-14 py-12 md:py-16">
        <div className="font-display tracking-[0.35em] text-[11px] text-accent-gold font-bold uppercase">
          Hot take graveyard
        </div>
        <h2 className="mt-2 font-display font-black uppercase leading-none text-4xl md:text-5xl">
          Sealed, for now.
        </h2>
        <p className="mt-4 max-w-2xl font-body italic text-ink-secondary">
          These lines are already on YouTube or in the ranking videos. The site is storing them
          so December has someone to laugh at. They are not official keepers, draft results, or
          league rulings.
        </p>
        <div className="mt-10 columns-1 md:columns-2 gap-4 space-y-4">
          {SEALED_TAKES.map((entry) => (
            <Reveal as="blockquote" key={entry.text} className="break-inside-avoid border-l-2 border-accent-gold pl-4 py-1">
              <p className="font-display font-black italic text-lg leading-snug">“{entry.text}”</p>
              <cite className="mt-2 block not-italic font-mono text-[10px] tracking-[0.18em] uppercase text-ink-tertiary">
                {entry.speaker}
              </cite>
            </Reveal>
          ))}
        </div>
        <div className="mt-10">
          <Link
            to="/predictions"
            className="inline-block font-display tracking-[0.25em] uppercase text-[11px] font-bold text-accent-gold border border-accent-gold px-5 py-3 hover:bg-accent-gold hover:text-bg-deep transition-colors"
          >
            Seal your own receipt →
          </Link>
        </div>
      </section>
    </>
  );
}

function HeroChip({ value, label }) {
  return (
    <div className="border border-accent-red/40 bg-bg-deep/50 px-3 py-3">
      <div className="font-display font-black uppercase leading-none text-xl">{value}</div>
      <div className="mt-1 font-mono text-[9px] tracking-[0.16em] uppercase text-ink-tertiary">{label}</div>
    </div>
  );
}
